import {
  BoundSelectField,
  BoundSelectFieldProps,
  BoundTextField,
  Css,
  IconButton,
  Palette,
  Tag,
  useComputed,
} from "@homebound/beam";
import { FieldState } from "@homebound/form-state";
import { Maybe } from "graphql/jsutils/Maybe";
import { useState } from "react";
import { LocationsSelectField_LocationFragment } from "src/components/autoPopulateSelects/LocationsSelectField.generated";
import { useAddEditItemModal_GetLocationsQuery } from "src/routes/libraries/plan-package/takeoffs/components/AddItems/AddEditItemLocationsField.generated";
type AddEditItemLocationsFieldProps = Omit<
  BoundSelectFieldProps<LocationsSelectField_LocationFragment, string>,
  // Omit options/etc b/c we'll drive those from a query, based on the rpavId
  "options" | "multiselect" | "getOptionLabel" | "getOptionValue"
> & {
  // Users cannot add new rooms, but they can add new features
  newFeatureNameField: FieldState<Maybe<string>>;
  rpavId: string;
};

/**
 * Combines two Location (Group/Room) + optional Feature fields into a single field.
 */
export function AddEditItemLocationsField(props: AddEditItemLocationsFieldProps) {
  const {
    placeholder = "Select a Location",
    onSelect,
    newFeatureNameField,
    field,
    rpavId,
    disabled,
    ...otherProps
  } = props;

  const { data, loading } = useAddEditItemModal_GetLocationsQuery({ variables: { rpavId } });

  // TODO: Update locationFilter to include type, sort by path
  // SC-55529 - https://app.shortcut.com/homebound-team/story/55529/chore-be-update-locationsfilter-to-support-type-and-sortby-path
  // @ts-ignore path is never undefined on v2 locations
  const locations = data?.locations.sortByKey("path") || [];

  const selectedLocation = useComputed(() => {
    return locations.find((l) => l.id === field.value);
  }, [field, locations]);
  const [showAddLocationField, setShowAddLocationField] = useState(false);

  return (
    <>
      <div css={Css.df.aic.gap2.$}>
        <BoundSelectField
          {...otherProps}
          field={field}
          label="Location"
          disabled={disabled}
          onSelect={(locationId, location) => {
            onSelect?.(locationId, location);
          }}
          placeholder={loading ? "Loading..." : placeholder}
          options={locations}
          getOptionValue={(o) => o.id}
          getOptionLabel={(o) => o.name}
          getOptionMenuLabel={({ name, displayLocationPath, type }) => (
            <span>
              <Tag xss={Css.m0.$} type="info" text={type.name} /> {name}{" "}
              {name !== displayLocationPath && <span css={Css.gray500.$}>{displayLocationPath}</span>}
            </span>
          )}
          hideErrorMessage
          autoSort={false}
        />
        {!showAddLocationField && (
          // Add top padding to center
          <div css={Css.ptPx(26).$}>
            <IconButton
              color={Palette.Blue500}
              data-testid="addSubLocationBtn"
              tooltip="Add new feature location"
              disabled={disabled ?? (!selectedLocation ? "Select a room location first" : false)}
              onClick={() => setShowAddLocationField(true)}
              icon="plus"
            />
          </div>
        )}
      </div>
      {showAddLocationField && (
        <div css={Css.df.aic.gap2.$}>
          <BoundTextField autoFocus label={`Feature of ${selectedLocation?.name}`} field={newFeatureNameField} />
          <div css={Css.ptPx(26).$}>
            <IconButton
              color={Palette.Blue500}
              tooltip="Remove child location"
              onClick={() => {
                newFeatureNameField.set(undefined);
                setShowAddLocationField(false);
              }}
              icon="x"
            />
          </div>
        </div>
      )}
    </>
  );
}
